import React, { useState, useEffect } from "react";
import { CheckCircleOutline } from "@mui/icons-material";
import { Box, useTheme, Avatar, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { largeBold, thinBold, typoBase, typoThin } from "../styles/typography";
import { fetchUsers } from "../redux/actions/authActions";
import { deleteTask, getTasksByCaseId } from '../redux/actions/taskActions';
import { useDispatch, useSelector } from "react-redux";

const formatDateTask = (dateString) => {
  if (!dateString) return '--';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString().slice(-2); // Get last 2 digits of year
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const calculateOverdueDays = (dueDate) => {
  const currentDate = new Date();
  const due = new Date(dueDate);
  const diffTime = Math.abs(currentDate - due);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const FileTaskCard = ({ filteredTasks }) => {
  const { t } = useTranslation(['common', 'pages']);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const theme = useTheme();
  const [filter, setFilter] = useState("All");
  const [hoveredTaskId, setHoveredTaskId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const { users, loading, error } = useSelector((state) => state.userList);

  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state

  const completedTasksCount = filteredTasks.filter(
    (task) => task.status === "Completed"
  ).length;
  const overdueTasksCount = filteredTasks.filter(
    (task) => new Date(task.dueDate) < new Date() && task.status !== "Completed"
  ).length;
  const upcomingTasksCount = filteredTasks.filter(
    (task) =>
      new Date(task.dueDate) >= new Date() && task.status !== "Completed"
  ).length;

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  const getUserAvatar = (taskOwnerId) => {
    const user = users.find((user) => user.relationship === taskOwnerId);
    return user ? user.avatar : null;
  };

  const handleDeleteClick = (taskId, e) => {
    e.stopPropagation();
    setTaskToDelete(taskId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (taskToDelete) {
      try {
        await dispatch(deleteTask(taskToDelete));
        await dispatch(getTasksByCaseId(id));
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
    setDeleteDialogOpen(false);
    setTaskToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setTaskToDelete(null);
  };

  const filteredAndSortedTasks = filteredTasks
    .filter((task) => {
      if (filter === "All") return true;
      if (filter === "Completed") return task.status === "Completed";
      if (filter === "Overdue")
        return (
          new Date(task.dueDate) < new Date() && task.status !== "Completed"
        );
      if (filter === "Upcoming")
        return (
          new Date(task.dueDate) >= new Date() && task.status !== "Completed"
        );
      return true;
    })
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-evenly",
          alignItems: "center",
          marginTop: "-10px",
        }}
      >
        <div
          style={{ textAlign: "center", cursor: "pointer" }}
          onClick={() => setFilter("All")}
        >
          <h4
            style={{
              color: "rgb(53, 53, 53)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "15px",
              fontWeight: 600,
              height: "19.5px",
              lineHeight: "19.5px",
            }}
          >
            {filteredTasks.length}
          </h4>
          <p
            style={{
              color: "rgb(117, 117, 117)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "11.5px",
              height: "15.75px",
              lineHeight: "15.75px",
              marginTop: "-15px", // Keeping the existing marginTop style
            }}
          >
            {t('common:all')}
          </p>
        </div>
        <div
          style={{ textAlign: "center", cursor: "pointer" }}
          onClick={() => setFilter("Completed")}
        >
          <h4
            style={{
              color: "rgb(53, 53, 53)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "15px",
              fontWeight: 600,
              height: "19.5px",
              lineHeight: "19.5px",
            }}
          >
            {completedTasksCount}
          </h4>
          <p
            style={{
              color: "rgb(117, 117, 117)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "11.5px",
              height: "15.75px",
              lineHeight: "15.75px",
              marginTop: "-15px", // Keeping the existing marginTop style
            }}
          >
            {t('common:completed')}
          </p>
        </div>
        <div
          style={{ textAlign: "center", cursor: "pointer" }}
          onClick={() => setFilter("Overdue")}
        >
          <h4
            style={{
              color: "rgb(53, 53, 53)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "15px",
              fontWeight: 600,
              height: "19.5px",
              lineHeight: "19.5px",
            }}
          >
            {overdueTasksCount}
          </h4>
          <p
            style={{
              color: "rgb(117, 117, 117)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "11.5px",
              height: "15.75px",
              lineHeight: "15.75px",
              marginTop: "-15px", // Keeping the existing marginTop style
            }}
          >
            {t('common:overdue')}
          </p>
        </div>
        <div
          style={{ textAlign: "center", cursor: "pointer" }}
          onClick={() => setFilter("Upcoming")}
        >
          <h4
            style={{
              color: "rgb(53, 53, 53)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "15px",
              fontWeight: 600,
              height: "19.5px",
              lineHeight: "19.5px",
            }}
          >
            {upcomingTasksCount}
          </h4>
          <p
            style={{
              color: "rgb(117, 117, 117)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "11.5px",
              height: "15.75px",
              lineHeight: "15.75px",
              marginTop: "-15px", // Keeping the existing marginTop style
            }}
          >
            {t('pages:tasks.to_do')}
          </p>
        </div>
      </div>
      <Box
        sx={{
          marginTop: 1,
          overflowY: "auto",
          maxHeight: "380px",
          "&::-webkit-scrollbar": {
            width: "5px", // Set the width of the scrollbar
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "lightgrey", // Set the color of the scrollbar thumb
            borderRadius: "0", // Set the border radius of the scrollbar thumb
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#888", // Set the color of the scrollbar thumb on hover
          },
        }}
      >
        {filteredAndSortedTasks && filteredAndSortedTasks.length > 0 ? (
          filteredAndSortedTasks.map((task) => (
          <Box
            key={task._id}
            onMouseEnter={() => setHoveredTaskId(task._id)}
            onMouseLeave={() => setHoveredTaskId(null)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid lightgrey",
              padding: "5px",
              paddingTop: "5px",
              "&:hover": {
                backgroundColor: "#ecf5fb",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleOutline
                    sx={{
                      color:
                        task.status === "Completed"
                          ? "rgb(133, 188, 32)"
                          : "lightgrey",

                      fontSize: "18px",
                      marginRight: "10px",
                    }}
                  />
                  <Box
                    sx={{
                      width: "10px",
                      height: "10px",
                      backgroundColor: task.colour,
                      marginRight: "5px",
                    }}
                  />
                  <Box
                    sx={{
                      color: "rgb(0, 91, 146)",
                      cursor: "pointer",
                      display: "block",
                      fontFamily:
                        '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                      fontSize: "11.5px",
                      fontWeight: 400,
                      height: "13.5px",
                      lineHeight: "13.5px",

                      paddingBottom: "2px",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                      maxWidth: "25ch",
                      marginTop: "4px",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() =>
                      navigate(`/task/${task._id}`, { state: { caseId: id } })
                    }
                  >
                    {task.subject}
                  </Box>
                </Box>
                <Box
                  sx={{
                    color: "#757575",
                    display: "block",
                    fontFamily:
                      '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                    fontSize: "10.5px",
                    fontWeight: 400,
                    height: "15.75px",
                    lineHeight: "15.75px",
                    marginLeft: "24px",
                  }}
                >
                  {formatDateTask(task.dueDate)}
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  alignItems: "flex-end",
                }}
              >
                <Box
                  sx={{
                    color: "rgb(53, 53, 53)",
                    fontFamily:
                      '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                    fontSize: "11.5px",
                    fontWeight: 400,
                    height: "15.75px",
                    lineHeight: "15.75px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  {task?.owner?.firstName + " " + task?.owner?.lastName}
                  <Avatar
                    alt={user?.firstName}
                    src={
                      getUserAvatar(task?.owner?._id)
                        ? getUserAvatar(task?.owner?._id)
                        : "/static/images/avatar/1.jpg"
                    }
                    sx={{
                      fontFamily:
                        '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                      fontSize: "9px",
                      height: "18px",
                      lineHeight: "18px",
                      width: "18px",
                      marginLeft: "8px",
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    color: "rgb(229, 32, 46)",
                    display: "block",
                    fontFamily:
                      '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                    fontSize: "10.5px",
                    fontWeight: 400,
                    height: "15.75px",
                    lineHeight: "15.75px",
                  }}
                >
                  {new Date(task.dueDate) < new Date() &&
                  task.status !== "Completed"
                    ? `${calculateOverdueDays(task.dueDate)} days late`
                    : null}
                </Box>
              </Box>
            </Box>
            {hoveredTaskId === task._id && (
              <IconButton
                size="small"
                onClick={(e) => handleDeleteClick(task._id, e)}
                sx={{
                  color: '#666',
                  '&:hover': {
                    color: '#d32f2f',
                  },
                  marginLeft: '8px',
                }}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            )}
          </Box>
          ))
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%" p={2}>
            <Typography variant="body1" style={{ color: '#777' }}>
              {t('no_data_available')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle>{t('common:confirm_delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('common:delete_confirmation_message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {t('common:cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            {t('common:delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FileTaskCard;
